import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Power, PowerOff, Mail, Bell, AlertTriangle, Check, Pencil } from "lucide-react";

const TRIGGER_LABELS = {
  deal_enters_stage: "When deal enters a specific stage",
  deal_enters_any_stage: "When deal enters ANY new stage",
  deal_created: "When a deal is created",
  deal_accepted: "When a deal is accepted",
  deal_rejected: "When a deal is rejected",
};

const RECIPIENT_LABELS = {
  deal_owner: "Deal Owner",
  triggering_user: "Person who triggered the event",
  specific_users: "Specific user(s)",
};

/**
 * Reusable notification-rule panel.
 * Props:
 *   scope: "deal" | "pipeline"
 *   dealId / pipelineId: scope target
 *   canManage: boolean — if false, list-only
 */
export default function NotificationRulesPanel({
  scope,
  dealId,
  pipelineId,
  canManage,
}) {
  const [rules, setRules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null); // CW6.2.7
  const [teamMembers, setTeamMembers] = useState([]);
  const [stages, setStages] = useState([]);

  const load = () => {
    const params = { scope };
    if (scope === "deal") params.deal_id = dealId;
    if (scope === "pipeline") params.pipeline_id = pipelineId;
    api.get("/notification-rules", { params }).then(({ data }) => setRules(data));
  };
  useEffect(() => { load(); }, [scope, dealId, pipelineId]); // eslint-disable-line

  useEffect(() => {
    if (canManage) {
      api.get("/team-members").then(({ data }) => setTeamMembers(data));
    }
    // load stages for the pipeline (for stage-based rules)
    const pid = pipelineId || null;
    if (pid) {
      api.get(`/pipelines/${pid}/stages`).then(({ data }) => setStages(data));
    } else if (scope === "deal" && dealId) {
      // resolve deal's pipeline_id
      api.get(`/deals/${dealId}`).then(({ data }) => {
        const pid2 = data?.deal?.pipeline_id;
        if (pid2) {
          api.get(`/pipelines/${pid2}/stages`).then(({ data: s }) => setStages(s));
        }
      });
    }
  }, [pipelineId, dealId, scope, canManage]);

  const toggle = async (rule) => {
    await api.patch(`/notification-rules/${rule.rule_id}`, {
      is_active: !rule.is_active,
    });
    load();
  };

  const del = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.rule_name}"?`)) return;
    try {
      await api.delete(`/notification-rules/${rule.rule_id}`);
      toast.success("Rule deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h3 className="cw-h3">Notification Rules</h3>
        {canManage && (
          <button onClick={() => setShowForm(true)}
            className="cw-btn-primary text-xs inline-flex items-center gap-1"
            data-testid={`new-rule-btn-${scope}`}>
            <Plus size={12} /> New Rule
          </button>
        )}
      </div>
      <p className="text-xs text-[var(--cw-muted)] mt-1">
        {scope === "deal"
          ? "Deal-only automation. Fires only for this deal. Auto-cleaned if the deal is deleted."
          : "Pipeline-wide automation. Fires for any deal in this pipeline that matches the trigger."}
      </p>

      <div className="mt-4 space-y-2" data-testid={`rules-list-${scope}`}>
        {rules.length === 0 && (
          <div className="cw-card p-6 text-center text-xs text-[var(--cw-muted)]">
            No rules yet.
          </div>
        )}
        {rules.map((r) => (
          <RuleRow
            key={r.rule_id}
            rule={r}
            canManage={canManage}
            stages={stages}
            teamMembers={teamMembers}
            onToggle={toggle}
            onDelete={del}
            onEdit={() => setEditingRule(r)}
          />
        ))}
      </div>

      {showForm && (
        <RuleFormModal
          scope={scope}
          dealId={dealId}
          pipelineId={pipelineId}
          stages={stages}
          teamMembers={teamMembers}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}

      {editingRule && (
        <RuleFormModal
          scope={scope}
          dealId={dealId}
          pipelineId={pipelineId}
          stages={stages}
          teamMembers={teamMembers}
          existingRule={editingRule}
          onClose={() => setEditingRule(null)}
          onCreated={() => { setEditingRule(null); load(); }}
        />
      )}
    </div>
  );
}

function RuleSendToBadge({ rule }) {
  const [mapped, setMapped] = useState(null);
  const sendEmail = !!rule.send_email;
  const sendInApp = !!rule.send_in_app;
  useEffect(() => {
    if (!sendEmail) return;
    api.get("/v62/mapping-check", { params: { trigger_type: rule.trigger_type } })
      .then(({ data }) => setMapped(!!data?.mapped))
      .catch(() => setMapped(null));
  }, [rule.trigger_type, sendEmail]);
  if (!sendInApp && !sendEmail) return null;
  return (
    <div className="text-xs text-[var(--cw-muted)] mt-1 inline-flex items-center gap-2 flex-wrap"
         data-testid={`rule-sendto-${rule.rule_id}`}>
      <span className="font-medium">Send to:</span>
      {sendInApp && (
        <span className="inline-flex items-center gap-1 cw-badge"
              style={{ background: "rgba(69,87,204,0.10)", color: "var(--cw-primary)" }}>
          <Bell size={10} /> In app
        </span>
      )}
      {sendEmail && (
        <span className="inline-flex items-center gap-1 cw-badge"
              style={mapped === false
                ? { background: "rgba(224,161,0,0.15)", color: "#7A5900" }
                : { background: "rgba(44,154,82,0.12)", color: "#1E6B36" }}
              data-testid={`rule-email-status-${rule.rule_id}`}>
          {mapped === false
            ? <><AlertTriangle size={10} /> Email · no template</>
            : <><Mail size={10} /> Email</>}
        </span>
      )}
    </div>
  );
}

function RuleRow({ rule, canManage, stages, teamMembers, onToggle, onDelete, onEdit }) {
  const stg = stages.find((s) => s.stage_id === rule.trigger_stage_id);
  const users = teamMembers.filter((t) => (rule.recipient_user_ids || []).includes(t.user_id));
  return (
    <div
      className="cw-card p-3"
      style={{ opacity: rule.is_active ? 1 : 0.55 }}
      data-testid={`rule-row-${rule.rule_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold">{rule.rule_name}</div>
            {!rule.is_active && (
              <span className="cw-badge" style={{ background: "rgba(0,0,0,0.06)", color: "#666" }}>Inactive</span>
            )}
            {rule.delay_days > 0 && (
              <span className="cw-badge" style={{ background: "rgba(243,191,92,0.2)", color: "#7A5900" }}>
                Delayed {rule.delay_days}d
              </span>
            )}
          </div>
          <div className="text-xs text-[var(--cw-muted)] mt-1">
            {TRIGGER_LABELS[rule.trigger_type] || rule.trigger_type}
            {rule.trigger_type === "deal_enters_stage" && stg ? ` → ${stg.stage_name}` : ""}
          </div>
          <div className="text-xs text-[var(--cw-muted)] mt-0.5">
            Notify:{" "}
            {(rule.recipient_categories || []).map((c) => RECIPIENT_LABELS[c]).join(", ")}
            {users.length > 0 && ` (${users.map((u) => u.full_name || u.email).join(", ")})`}
          </div>
          {rule.notification_title && (
            <div className="text-xs mt-1"><strong>Title:</strong> {rule.notification_title}</div>
          )}
          {/* v6.2 — Send to column */}
          <RuleSendToBadge rule={rule} />
        </div>
        {canManage && (
          <div className="flex gap-1 shrink-0">
            <button onClick={onEdit}
              className="cw-btn-ghost text-[10px] inline-flex items-center gap-1"
              title="Edit"
              aria-label="Edit"
              data-testid={`rule-edit-${rule.rule_id}`}>
              <Pencil size={10} />
            </button>
            <button onClick={() => onToggle(rule)}
              className="cw-btn-ghost text-[10px] inline-flex items-center gap-1"
              data-testid={`rule-toggle-${rule.rule_id}`}>
              {rule.is_active ? <><PowerOff size={10} /> Disable</> : <><Power size={10} /> Enable</>}
            </button>
            <button onClick={() => onDelete(rule)}
              className="cw-btn-ghost text-[10px] text-[var(--cw-error)]"
              data-testid={`rule-delete-${rule.rule_id}`}>
              <Trash2 size={10} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function RuleFormModal({
  scope, dealId, pipelineId, stages, teamMembers, onClose, onCreated, existingRule,
}) {
  const isEdit = !!existingRule;
  const [form, setForm] = useState(() => existingRule ? {
    scope: existingRule.scope,
    scope_deal_id: existingRule.scope_deal_id || null,
    scope_pipeline_id: existingRule.scope_pipeline_id || null,
    rule_name: existingRule.rule_name || "",
    trigger_type: existingRule.trigger_type || "deal_enters_stage",
    trigger_stage_id: existingRule.trigger_stage_id || "",
    delay_days: existingRule.delay_days || 0,
    recipient_categories: existingRule.recipient_categories || ["deal_owner"],
    recipient_user_ids: existingRule.recipient_user_ids || [],
    notification_title: existingRule.notification_title || "",
    notification_body: existingRule.notification_body || "",
    is_active: existingRule.is_active,
    send_in_app: !!existingRule.send_in_app,
    send_email: !!existingRule.send_email,
  } : {
    scope,
    scope_deal_id: dealId || null,
    scope_pipeline_id: pipelineId || null,
    rule_name: "",
    trigger_type: "deal_enters_stage",
    trigger_stage_id: "",
    delay_days: 0,
    recipient_categories: ["deal_owner"],
    recipient_user_ids: [],
    notification_title: "",
    notification_body: "",
    is_active: true,
    // v6.2 — Send to channels. New rules default to both OFF per spec;
    // at least one must be ticked to save.
    send_in_app: false,
    send_email: false,
  });
  const [saving, setSaving] = useState(false);
  const [mapped, setMapped] = useState(null); // yes / no / null (unknown)

  // v6.2 — re-check email template mapping when trigger changes (only if
  // Email is currently ticked so we don't spam the endpoint).
  useEffect(() => {
    if (!form.send_email) { setMapped(null); return; }
    let cancelled = false;
    api.get("/v62/mapping-check", { params: { trigger_type: form.trigger_type } })
      .then(({ data }) => { if (!cancelled) setMapped(!!data?.mapped); })
      .catch(() => { if (!cancelled) setMapped(null); });
    return () => { cancelled = true; };
  }, [form.trigger_type, form.send_email]);

  const toggleCat = (cat) => {
    setForm((s) => {
      const has = s.recipient_categories.includes(cat);
      const next = has
        ? s.recipient_categories.filter((c) => c !== cat)
        : [...s.recipient_categories, cat];
      return { ...s, recipient_categories: next };
    });
  };
  const toggleUser = (uid_) => {
    setForm((s) => {
      const has = s.recipient_user_ids.includes(uid_);
      const next = has
        ? s.recipient_user_ids.filter((u) => u !== uid_)
        : [...s.recipient_user_ids, uid_];
      return { ...s, recipient_user_ids: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    // v6.2 — Send to save-guard (client-side mirror of backend 400).
    if (!form.send_in_app && !form.send_email) {
      toast.error("Pick In app, Email, or both under Send to.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.trigger_type !== "deal_enters_stage") payload.trigger_stage_id = null;
      if (!payload.recipient_categories.includes("specific_users")) payload.recipient_user_ids = [];
      if (["deal_created", "deal_accepted", "deal_rejected"].includes(payload.trigger_type)) {
        payload.delay_days = 0;
      }
      if (isEdit) {
        // CW6.2.7: edit same rule (no delete+recreate). Omit scope fields.
        const { scope: _s, scope_deal_id: _d, scope_pipeline_id: _p, ...patch } = payload;
        await api.patch(`/notification-rules/${existingRule.rule_id}`, patch);
        toast.success("Rule updated");
      } else {
        await api.post("/notification-rules", payload);
        toast.success("Rule created");
      }
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full h-9 px-3 rounded-md border text-sm mt-1";
  const border = { borderColor: "var(--cw-border)" };
  const isStageTrigger =
    form.trigger_type === "deal_enters_stage" || form.trigger_type === "deal_enters_any_stage";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="rule-form-modal">
      <form onSubmit={submit}
        className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between" style={border}>
          <h2 className="cw-h2">{isEdit ? "Edit Notification Rule" : "New Notification Rule"}</h2>
          <button type="button" onClick={onClose} className="text-[var(--cw-muted)] text-lg">×</button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-3">
          <div>
            <div className="cw-caption">Rule name *</div>
            <input required value={form.rule_name}
              onChange={(e) => setForm({ ...form, rule_name: e.target.value })}
              className={inp} style={border} data-testid="rule-name-input" />
          </div>
          <div>
            <div className="cw-caption">Trigger *</div>
            <select value={form.trigger_type}
              onChange={(e) => setForm({ ...form, trigger_type: e.target.value })}
              className={inp} style={border} data-testid="rule-trigger-select">
              {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          {form.trigger_type === "deal_enters_stage" && (
            <div>
              <div className="cw-caption">Stage *</div>
              <select required value={form.trigger_stage_id}
                onChange={(e) => setForm({ ...form, trigger_stage_id: e.target.value })}
                className={inp} style={border} data-testid="rule-stage-select">
                <option value="">Select…</option>
                {stages.map((s) => (
                  <option key={s.stage_id} value={s.stage_id}>{s.stage_name}</option>
                ))}
              </select>
            </div>
          )}
          {isStageTrigger && (
            <div>
              <div className="cw-caption">Delay (days) — fire only if the deal is still in this stage after N days</div>
              <input type="number" min={0} max={365} value={form.delay_days}
                onChange={(e) => setForm({ ...form, delay_days: parseInt(e.target.value || "0", 10) })}
                className={inp} style={border} data-testid="rule-delay-input" />
              <div className="text-[10px] text-[var(--cw-muted)] mt-1">
                Set to 0 for immediate fire. Verified against current deal state at fire time.
              </div>
            </div>
          )}
          <div>
            <div className="cw-caption">Recipients *</div>
            <div className="mt-1 space-y-1">
              {Object.entries(RECIPIENT_LABELS).map(([k, v]) => (
                <label key={k} className="flex items-center gap-2 text-xs">
                  <input type="checkbox"
                    checked={form.recipient_categories.includes(k)}
                    onChange={() => toggleCat(k)}
                    data-testid={`rule-recip-${k}`} />
                  {v}
                </label>
              ))}
            </div>
          </div>
          {form.recipient_categories.includes("specific_users") && (
            <div>
              <div className="cw-caption">Choose users</div>
              <div className="mt-1 max-h-40 overflow-y-auto border rounded-md p-2 space-y-1"
                style={border}>
                {teamMembers.map((m) => (
                  <label key={m.user_id} className="flex items-center gap-2 text-xs">
                    <input type="checkbox"
                      checked={form.recipient_user_ids.includes(m.user_id)}
                      onChange={() => toggleUser(m.user_id)} />
                    {m.full_name || m.email}
                  </label>
                ))}
              </div>
            </div>
          )}
          {/* v6.2 — Send to */}
          <div data-testid="rule-sendto-section">
            <div className="cw-caption">Send to *</div>
            <div className="mt-1 space-y-1">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox"
                  checked={form.send_in_app}
                  onChange={(e) => setForm({ ...form, send_in_app: e.target.checked })}
                  data-testid="rule-sendto-inapp" />
                In app
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox"
                  checked={form.send_email}
                  onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                  data-testid="rule-sendto-email" />
                Email
              </label>
            </div>
            {form.send_email && mapped === true && (
              <div className="mt-1 text-[11px] inline-flex items-center gap-1"
                   style={{ color: "#1E6B36" }} data-testid="rule-mapping-ok">
                <Check size={12} /> An email template is set up for this trigger.
              </div>
            )}
            {form.send_email && mapped === false && (
              <div className="mt-1 text-[11px] inline-flex items-start gap-1"
                   style={{ color: "#7A5900" }} data-testid="rule-mapping-missing">
                <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                <span>
                  No email template is set up for this trigger yet, so emails won't send. In-app still works. Ask your CompeteWiser admin to set it up.
                </span>
              </div>
            )}
          </div>
          <div>
            <div className="cw-caption">Notification title (optional)</div>
            <input value={form.notification_title}
              onChange={(e) => setForm({ ...form, notification_title: e.target.value })}
              className={inp} style={border} placeholder={`Rule: ${form.rule_name || "…"}`} />
          </div>
          <div>
            <div className="cw-caption">Notification body (optional)</div>
            <textarea value={form.notification_body}
              onChange={(e) => setForm({ ...form, notification_body: e.target.value })}
              className={inp + " min-h-[60px] py-2"} style={border} />
          </div>
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2" style={border}>
          <button type="button" onClick={onClose} className="cw-btn-ghost text-sm">Cancel</button>
          <button disabled={saving} type="submit" className="cw-btn-primary text-sm"
            data-testid="rule-submit">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Rule"}
          </button>
        </div>
      </form>
    </div>
  );
}
